/*
 * cw-variant-gallery.js — show only the selected colour's images in the product gallery.
 *
 * Reads the JSON config emitted next to the gallery, then on every variant change shows
 * only the media group matched to the selected variant:
 *  - main gallery slides + thumbnails (tagged server-side with data-cw-vg)
 *  - slider dots (first N kept, N = visible slide count — SliderComponent maps dots to page index)
 *  - zoom-modal media (tagged here from the config media map)
 *  - any other section element carrying data-cw-vg (e.g. description image blocks)
 * Other sections can follow along via the "cw:vg-change" document event (detail: {group, sectionId}).
 *
 * Two config shapes are supported:
 *  - OPTION mode (snippets/cw-variant-gallery-map.liquid): {optionIndex, values, active, media}
 *    the group is the handle of the selected variant's value for that option.
 *  - FILENAME mode (sections/cw-main-product-vg.liquid): {g1Match, g2Match, initial, media}
 *    the group is derived from the selected variant's featured image file name.
 *
 * The gallery keeps the customer's POSITION across a variant switch: viewing the 3rd gold
 * image and picking silver lands on the 3rd silver image (groups are parallel image sets).
 * Position is snapshotted in a capture-phase change listener — it must run before Dawn's own
 * picker handler, whose updateMedia() prepends the featured slide and scrolls back to it.
 *
 * keepOrder (OPTION mode): Dawn's updateMedia() moves the variant's featured slide + thumbnail
 * to the front on every switch. Server-side the filtered gallery renders the product's own media
 * order (see product-media-gallery.liquid), so the order is snapshotted at boot and put back
 * after each switch — each colour keeps opening on its photo 1, not on its variant image.
 */
(function () {
  'use strict';

  function slugify(value) {
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // FILENAME mode: which group does this image URL belong to?
  function groupOfSrc(cfg, src) {
    if (!src) return 'all';
    var file = String(src).split('?')[0].split('/').pop().toLowerCase();
    if (cfg.g1Match && file.indexOf(cfg.g1Match) > -1) return 'g1';
    if (cfg.g2Match && file.indexOf(cfg.g2Match) > -1) return 'g2';
    return 'all';
  }

  function groupOfVariant(cfg, variant) {
    if (!variant) return null;
    if (cfg.optionIndex) {
      var raw = variant.options && variant.options[cfg.optionIndex - 1];
      if (raw === undefined || raw === null || raw === '') return 'all';
      var mapped = cfg.values && Object.prototype.hasOwnProperty.call(cfg.values, raw)
        ? cfg.values[raw]
        : slugify(raw);
      return mapped || 'all';
    }
    if (variant.featured_media && variant.featured_media.preview_image) {
      return groupOfSrc(cfg, variant.featured_media.preview_image.src);
    }
    return 'all';
  }

  function initOne(cfgEl) {
    if (cfgEl.getAttribute('data-cw-vg-ready') === 'true') return;
    cfgEl.setAttribute('data-cw-vg-ready', 'true');

    var cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      return;
    }
    var sectionId = cfgEl.getAttribute('data-cw-vg-section-id') || cfg.sectionId;
    var root = cfgEl.closest('.shopify-section') || document;
    var state = cfg.active || cfg.initial || 'all';
    if (!state) state = 'all';
    var keepOrder = cfg.keepOrder === true;

    // Snapshot the server-rendered (product) order before any variant change can reorder it.
    if (keepOrder) {
      root.querySelectorAll('media-gallery').forEach(function (gal) {
        gal.__cwVgOrder = [
          Array.prototype.slice.call(gal.querySelectorAll('.product__media-item')),
          Array.prototype.slice.call(gal.querySelectorAll('.thumbnail-list__item'))
        ];
      });
    }

    // Put slides / thumbnails back in product order after Dawn prepends the featured media.
    function restoreOrder(gal) {
      if (!gal.__cwVgOrder) return;
      gal.__cwVgOrder.forEach(function (list) {
        if (!list.length) return;
        var parent = list[0].parentElement;
        if (!parent) return;
        var inOrder = list.every(function (el, i) {
          return parent.children[i] === el;
        });
        if (inOrder) return;
        list.forEach(function (el) {
          parent.appendChild(el);
        });
      });
    }

    // The zoom modal renders plain data-media-id elements — tag them from the media map.
    root.querySelectorAll('.product-media-modal__content [data-media-id]').forEach(function (el) {
      var g = cfg.media && cfg.media[String(el.getAttribute('data-media-id'))];
      if (g) el.setAttribute('data-cw-vg', g);
    });

    function visibleSlides(gal) {
      return Array.prototype.filter.call(gal.querySelectorAll('.product__media-item'), function (s) {
        return !s.hasAttribute('data-cw-vg-hidden');
      });
    }

    // Index of the slide the customer is looking at, among the currently visible slides.
    // Mobile slider: derive from scroll offset (swiping never updates .is-active).
    // Desktop thumbnail layouts: the viewer doesn't scroll (inactive slides are display:none),
    // so fall through to the .is-active slide.
    function currentIndex(gal) {
      var vis = visibleSlides(gal);
      if (vis.length < 2) return 0;
      var slider = gal.querySelector('[id^="GalleryViewer"] [id^="Slider-"]');
      if (slider && slider.scrollWidth - slider.clientWidth > 1) {
        var offset = vis[1].offsetLeft - vis[0].offsetLeft;
        if (offset > 0) {
          return Math.max(0, Math.min(vis.length - 1, Math.round(slider.scrollLeft / offset)));
        }
      }
      var active = gal.querySelector('.product__media-item.is-active');
      var i = vis.indexOf(active);
      return i > -1 ? i : 0;
    }

    function trimDots() {
      root.querySelectorAll('media-gallery').forEach(function (gal) {
        var visible = visibleSlides(gal).length;
        gal.querySelectorAll('[data-cw-vg-dot]').forEach(function (dot, i) {
          dot.toggleAttribute('data-cw-vg-hidden', i >= visible);
        });
      });
    }

    function refreshSliders() {
      if (keepOrder) root.querySelectorAll('media-gallery').forEach(restoreOrder);
      root.querySelectorAll('slider-component').forEach(function (sc) {
        if (typeof sc.resetPages === 'function') sc.resetPages();
      });
      root.querySelectorAll('media-gallery').forEach(function (gal) {
        var vis = visibleSlides(gal);
        if (!vis.length || typeof gal.setActiveMedia !== 'function') return;
        var target = null;
        if (typeof gal.__cwVgSavedIndex === 'number') {
          // Variant switch: land on the same position within the new group.
          target = vis[Math.min(gal.__cwVgSavedIndex, vis.length - 1)];
          gal.__cwVgSavedIndex = null;
          if (target.classList.contains('is-active') && vis.indexOf(target) === 0) target = null;
        } else {
          var active = gal.querySelector('.product__media-item.is-active');
          if (active && active.hasAttribute('data-cw-vg-hidden')) target = vis[0];
        }
        if (target) gal.setActiveMedia(target.getAttribute('data-media-id'), false);
      });
    }

    function apply(group) {
      state = group;
      root.querySelectorAll('[data-cw-vg]').forEach(function (el) {
        var g = el.getAttribute('data-cw-vg');
        var hide = g !== 'all' && group !== 'all' && g !== group;
        el.toggleAttribute('data-cw-vg-hidden', hide);
      });
      trimDots();
      requestAnimationFrame(refreshSliders);
      document.dispatchEvent(
        new CustomEvent('cw:vg-change', { detail: { group: group, sectionId: sectionId } })
      );
    }

    // Capture phase: runs BEFORE Dawn's picker handler moves is-active / prepends the
    // featured slide / scrolls — the only moment the pre-switch position is still readable.
    root.addEventListener(
      'change',
      function (e) {
        var t = e.target;
        if (!t || !t.closest || !t.closest('variant-radios, variant-selects')) return;
        root.querySelectorAll('media-gallery').forEach(function (gal) {
          gal.__cwVgSavedIndex = currentIndex(gal);
        });
      },
      true
    );

    root.addEventListener('change', function (e) {
      var picker = e.target && e.target.closest ? e.target.closest('variant-radios, variant-selects') : null;
      if (!picker) return;
      // Runs after the picker's own handler (element listeners fire before this bubbled one),
      // so currentVariant and Dawn's updateMedia() are already done. setTimeout for extra safety.
      setTimeout(function () {
        var g = groupOfVariant(cfg, picker.currentVariant);
        // keepOrder: a same-colour variant (e.g. a size) can also carry a featured image that
        // Dawn just moved to the front, so re-apply to put the order back.
        if (g && (g !== state || keepOrder)) apply(g);
      }, 0);
    });

    // Initial pass: server-side render already hid the inactive group; this tags the modal,
    // trims dots after any client-side reordering, and tells listening sections the start group.
    apply(state);
  }

  function boot() {
    document.querySelectorAll('[data-cw-vg-config]').forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function () {
    setTimeout(boot, 0);
  });
})();
